import { useAuth0 } from "contexts/AuthManager/Auth";
import {
  useSettings,
  defaultSettingsContext,
} from "contexts/SettingsManager/SettingsManager";
import { useMount } from "constants/constants";
import { useEffect } from "react";
import LogRocket from "logrocket";
import * as Sentry from "@sentry/react";
import { resetAllRetriesFromLocalStorage } from "contexts/ErrorManager/errorManagerUtils";
import {
  useEffectOnce,
  useGetPageFromPath,
  useSummaryGroup,
} from "utils/customHooks/genericHooks";
import {
  getIsFirstTimeSetup,
  applySettingsFromLocalStorage,
  applySettingsFromQuery,
  completeFirstTimeSetup,
  useEnsurePreferredMetricIsVisibleInMetricRow,
} from "./initializeAppHooksUtils";
import { SHOULD_TRACE_NETWORK_REQUESTS } from "../components/Settings/settingsUtils";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  hasLDClientBeenIdentifiedAtom,
  useSyncEditableFlagsToLDFlagsOncePerCustomer,
} from "utils/customHooks/useFlagsWithDefaults";
import { useAtom } from "jotai";
import { usePopulateCustomerJsonsInAtom } from "components/Dashboard/Tiles/CustomerTiles/CustomerTilesInternalTools/CustomerInfoIndicator";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { useIsDarkMode } from "store/atoms";
import {
  useFlightConfig,
  useIsFlightConfigInitialized,
} from "components/DataGetter/FlightConfig/useFlightConfig";
import { DataDumpAggregateType } from "types/types";
import { useVisibleMetrics } from "components/Dashboard/GroupMetrics/useVisibleMetrics";

/** useMounts (runs once each):
 * 1. remove expired tracing flag
 * 2. record the login date, and reset error retries
 * 3. once we have the ckey...
 *   a. apply local storage settings
 *   b. override any local storage settings coming in from the url query
 *   c. one-time setup: hide metrics for customers who hide metrics
 *   d. flag one-time setup as completed
 *
 * useEffects (runs whenever something changes):
 * - whenever preferredMetric changes, make sure the associated "isThisMetricVisibleMetricRow" is true
 * - whenever the user changes, send user info to LogRocket and Sentry
 * - whenever the user hits Tab key, remove outline: none CSS property to enable keyboard accessibility
 */
// tslint:disable-next-line: cognitive-complexity
function useInitializeApp() {
  const { user } = useAuth0();
  const {
    setIsLargeTiles,
    isLargeTiles,
    setIsRolledUpTiles,
    isRolledUpTiles,
    isTourEnabled,
    setIsTourEnabled,
    isPnlBreakdownChartVisible,
    setIsPnlBreakdownChartVisible,
    settingsToApply,
    setDayStartMsOffset,
  } = useSettings();

  // when isLargeTiles becomes true, add "large-tiles" class to the body, so we can target this mode with CSS e.g. in flightConfig.general.customCss
  useEffect(() => {
    document.body.classList.toggle("large-tiles", isLargeTiles);
    return () => {
      document.body.classList.remove("large-tiles");
    };
  }, [isLargeTiles]);

  const [isDarkMode] = useIsDarkMode();
  // toggle class on #root to produce some otherwise-inaccessible styles in index.css
  useEffect(() => {
    const rootNode = document.getElementById("root");
    if (!rootNode) {
      return;
    }
    if (isDarkMode) {
      rootNode.classList.add("dark-mode");
    } else {
      rootNode.classList.remove("dark-mode");
    }
  }, [isDarkMode]);

  // if there's a new entry in expandedProcessTileConfig since the last flight release,
  // remove the setting from localStorage
  useMount(() => {
    const expandedTileConfigLS = window.localStorage.getItem(
      "expandedProcessTileConfig"
    );
    const expandedTileConfig =
      expandedTileConfigLS && JSON.parse(expandedTileConfigLS);
    if (
      expandedTileConfig &&
      Object.keys(expandedTileConfig).length !==
        Object.keys(defaultSettingsContext.expandedProcessTileConfig).length
    ) {
      window.localStorage.removeItem("expandedProcessTileConfig");
    }
  });

  // record the login date, and reset error retries
  useMount(() => {
    window.localStorage.setItem("loginDateMs", String(Date.now()));
    resetAllRetriesFromLocalStorage(); // reset number of error_retries when app mounts
  });

  const ckey = useCustomerKey();

  // apply settings from local storage, then override any from the query
  // (this will only run once, when we have the ckey)
  useEffectOnce({
    shouldRun: Boolean(ckey),
    callback: () => {
      /**
       * If it's the user's first time in the app, then we need to initialise
       * the local storage settings to be the default setting values
       * (only need to ever do this once on each device / browser)
       */
      const isFirstTimeSetup = getIsFirstTimeSetup();
      applySettingsFromLocalStorage(settingsToApply, ckey, isFirstTimeSetup);
      applySettingsFromQuery({
        isLargeTiles: [isLargeTiles, setIsLargeTiles],
        isRolledUpTiles: [isRolledUpTiles, setIsRolledUpTiles],
        isTourEnabled: [isTourEnabled, setIsTourEnabled],
        isPnlBreakdownChartVisible: [
          isPnlBreakdownChartVisible,
          setIsPnlBreakdownChartVisible,
        ],
      });

      // first time setup is complete
      completeFirstTimeSetup();
    },
    dependencies: [ckey],
  });

  // remove outline: none CSS property when user hits Tab
  // https://hackernoon.com/removing-that-ugly-focus-ring-and-keeping-it-too-6c8727fefcd2
  useMount(() => {
    const handleFirstTab = e => {
      if (e.keyCode === 9) {
        // the "I am a keyboard user" key
        document.body.classList.add("user-is-tabbing");
        window.removeEventListener("keydown", handleFirstTab);
      }
    };

    window.addEventListener("keydown", handleFirstTab);
    return () => {
      // the App shouldn't ever unmount... just putting this here as a memory leak best-practices pattern
      window.removeEventListener("keydown", handleFirstTab);
    };
  });

  // identify user for LogRocket and Sentry
  useEffect(() => {
    if (user?.email) {
      LogRocket.identify(user.email, {
        name: user.name ?? "",
        email: user.email,
        // Add your own custom user variables here, ie:
        // subscriptionType: 'pro'
      });
      Sentry.setUser({
        username: user.name,
        email: user.email,
      });
    }
  }, [user?.email, user?.name]);

  // if the current preferredMetric is not available to this customer,
  // reset the preferredMetric to Uptime or OEE.
  useResetPreferredMetricToAllowedValue();

  // when the preferred metric changes,
  // make sure the associated metric is visible in GroupMetrics
  useEnsurePreferredMetricIsVisibleInMetricRow();

  // sync localStorage "should_trace_network" flag with launchDarkly
  // we use localStorage because we need to access it in places where hooks aren't allowed (AskrProvider, MachemetryProvider, MidgardProvider)
  useSyncForceTraceFlagToLS();

  useSyncEditableFlagsToLDFlagsOncePerCustomer();

  // grab customer JSON for current customer (contains customer timezone info)
  usePopulateCustomerJsonsInAtom(
    ckey ? [{ abbrevName: ckey, customerName: "" }] : []
  );

  useLaunchDarklyIdentifyUser();

  // when the ckey changes, set isFlightConfigInitialized back to null
  const [isFlightConfigInitialized, setIsFlightConfigInitialized] =
    useIsFlightConfigInitialized();
  useEffect(() => {
    setIsFlightConfigInitialized("unknown");
  }, [ckey, setIsFlightConfigInitialized]);

  // hydrate settings once when we receive flightConfig
  const [flightConfig] = useFlightConfig();
  useEffectOnce({
    callback: () => {
      const { dayStartMsOffset } = flightConfig.general;
      if (dayStartMsOffset || dayStartMsOffset === 0) {
        setDayStartMsOffset(dayStartMsOffset);
      }
    },
    shouldRun: isFlightConfigInitialized === "initialized",
    dependencies: [isFlightConfigInitialized],
  });
}

function useSyncForceTraceFlagToLS() {
  // we can grab forceTrace directly from useFlags, and coerce to boolean if it's undefined
  const flags = useFlags();
  useEffect(() => {
    const { forceTrace } = flags;
    window.localStorage.setItem(
      SHOULD_TRACE_NETWORK_REQUESTS,
      String(Boolean(forceTrace))
    );
  }, [flags]);
}

export default useInitializeApp;

/** identify the user for our Launch Darkly feature flag client */
function useLaunchDarklyIdentifyUser() {
  const ldClient = useLDClient();
  const { user } = useAuth0();
  const ckey = useCustomerKey();
  const [, setHasLDClientBeenIdentified] = useAtom(
    hasLDClientBeenIdentifiedAtom
  );
  const { pageName } = useGetPageFromPath();
  const currentProcessId = useProcessIdOnSingleProcessPage();
  const summaryGroup = useSummaryGroup();
  // when ckey, email, or currentProcessId changes, update our LaunchDarkly flags
  useEffect(() => {
    const ready = user?.email;
    if (ready && ldClient) {
      ldClient
        .identify({
          kind: "user",
          key: user?.email,
          email: user?.email,
          customer: ckey,
          hostname: window.location.hostname,
          currentProcessId: currentProcessId ?? "",
          pageName,
          summaryGroup,
          url: window.location.href,
        })
        .then(() => {
          setHasLDClientBeenIdentified(true);
          console.log(
            `Identified user for LaunchDarkly, email: ${user?.email}, ckey: ${ckey}`
          );
        });
    } else {
      console.log(
        `Could not identify user for LaunchDarkly, email: ${user?.email}, ckey: ${ckey}`
      );
    }
  }, [
    pageName,
    ckey,
    ldClient,
    user?.email,
    setHasLDClientBeenIdentified,
    currentProcessId,
    summaryGroup,
  ]);

  // when the ckey changes, LD client must be identified again
  useEffect(() => {
    setHasLDClientBeenIdentified(false);
  }, [ckey, setHasLDClientBeenIdentified]);
}
/** on mount, if preferredMetric is unset or invalid, we'll set it to the highest valid
 * metric in the PREFERRED_METRIC_PREFERENCE_ORDER array
 */
const PREFERRED_METRIC_PREFERENCE_ORDER: (keyof DataDumpAggregateType)[] = [
  "oeePercent",
  "uptimePercent",
  "teepPercent",
  "availabilityPercent",
];

/**
 * If the current preferredMetric is not available to this customer,
 * reset the preferredMetric to Uptime or OEE. This is mostly to
 * prevent the metric from being undefined for internal users
 * as they change customers in the app
 */
function useResetPreferredMetricToAllowedValue() {
  const { preferredMetric, setPreferredMetric } = useSettings();
  const visibleMetrics = useVisibleMetrics();
  const { isSettingsPage } = useGetPageFromPath();
  useEffect(() => {
    if (isSettingsPage) {
      return;
    }
    // GroupMetrics shows metrics which can be used as preferredMetric
    const visibleMetricKeys = visibleMetrics.GroupMetrics.map(
      d => d.dataDumpKey
    ).filter(Boolean);
    const isValidPreferredMetric = visibleMetricKeys.includes(preferredMetric);
    if (!isValidPreferredMetric) {
      const preferredPreferredMetric = PREFERRED_METRIC_PREFERENCE_ORDER.find(
        metric => visibleMetricKeys.includes(metric)
      );
      if (preferredPreferredMetric) {
        setPreferredMetric(preferredPreferredMetric);
      }
    }
  }, [
    isSettingsPage,
    preferredMetric,
    setPreferredMetric,
    visibleMetrics.GroupMetrics,
  ]);
}
